import React, { useState, useEffect, useRef } from 'react'
import { pdfjs } from 'react-pdf'
import cn from 'classnames'
import Toolbar from './component/toolbar'
import classes from './style/index.styl'
import { Viewer } from './component/viewer'


// set pdf.worker.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${ pdfjs.version }/pdf.worker.js`


export interface PDFViewerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * url of pdf file
   */
  fileUrl: string
  /**
   * filename of pdf file
   */
  fileName?: string
  /**
   * the text direction
   * @default 'ltr'
   */
  direction?: 'ltr' | 'rtl'
  /**
   * minimum scaling
   * @default 0.50
   */
  MIN_SCALING?: number
  /**
   * maximum scaling
   * @default 4.00
   */
  MAX_SCALING?: number
  /**
   * The size of the zoom step
   * @default 0.1
   */
  ZOOM_STEP?: number
  /**
   * 缩放比例下拉框的可选项
   * @default [ 0.50, 0.75, 1.00, 1.25, 1.50, 2.00, 3.00, 4.00 ]
   */
  scaleOptions?: number[]
  /**
   * Function called in case of an error while loading a document.
   */
  onDocumentLoadError?: (error: Error) => void
  /**
   * Function called when the document is successfully loaded.
   */
  onDocumentLoadSuccess?: (pdf: pdfjs.PDFDocumentProxy) => void
}


const fileNameRegex = /(?:[^/\\]*[/\\])?([^/\\]+?)(?:#[\s\S]*)?$/
export function PDFViewer(props: PDFViewerProps): React.ReactElement {
  const {
    fileUrl,
    fileName: defaultFileName,
    direction,
    className,
    MIN_SCALING,
    MAX_SCALING,
    ZOOM_STEP,
    scaleOptions,
    onDocumentLoadError,
    onDocumentLoadSuccess,
    ...restProps
  } = props

  const directionClassName = direction === 'rtl' ? classes.rtl : classes.ltr
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [pageTotal, setPageTotal] = useState<number>(0)
  const [scale, setScale] = useState<number>(1.0)
  const [fileName, setFileName] = useState<string>(defaultFileName || '')
  const containerRef = useRef<HTMLDivElement>(null)

  // 当文件 url 发生变化时，需要重新加载文档
  useEffect(() => {
    setPageNumber(1)
    setPageTotal(0)

    let newFileName: string = defaultFileName as string
    if (newFileName == null) {
      const m = fileNameRegex.exec(fileUrl)
      if (m == null) newFileName = ''
      else newFileName = m[1]
    }
    setFileName(newFileName)
  }, [fileUrl, defaultFileName])

  const handleDocumentLoadSuccess = (pdf: pdfjs.PDFDocumentProxy) => {
    setPageTotal(pdf.numPages)

    // auto scale
    pdf.getPage(1).then(page => {
      console.log('containerRef.current == null:', containerRef.current == null)
      if (containerRef.current == null) return
      const { width } = page.getViewport({ scale })
      const offsetWidth = containerRef.current.offsetWidth
      const newScale = Math.min(1.0, Math.floor(offsetWidth * 100 / width) / 100)
      console.log('width:', width, 'offsetWidth:', offsetWidth, 'newScale:', newScale)
      setScale(newScale)
    })
  }

  const handleDocumentLoadError = (error: Error) => {
    if (onDocumentLoadError != null) {
      onDocumentLoadError(error)
      return
    }
    console.error('[document loadError]:', error)
  }

  return (
    <div className={ cn(className, classes.rootContainer) } { ...restProps }>
      <div className={ cn(classes.outerContainer, directionClassName) }>
        <div ref={ containerRef } className={ cn(classes.mainContainer) }>
          <Toolbar
            pageNumber={ pageNumber }
            pageTotal={ pageTotal }
            scale={ scale }
            fileUrl={ fileUrl }
            fileName={ fileName }
            MIN_SCALING={ MIN_SCALING }
            MAX_SCALING={ MAX_SCALING }
            ZOOM_STEP={ ZOOM_STEP }
            scaleOptions={ scaleOptions }
            onPageNumberChange={ nextPageNo => {
              // check the nextPageNo is within the interval [1, pageTotal]
              if (Number.isNaN(nextPageNo) || nextPageNo <= 0 || nextPageNo > pageTotal) return
              setPageNumber(nextPageNo)
            } }
            onScaleChange={ nextScale => setScale(nextScale) }
          />
          <Viewer
            fileUrl={ fileUrl }
            pageTotal={ pageTotal }
            pageNumber={ pageNumber }
            scale={ scale }
            onDocumentLoadSuccess={ handleDocumentLoadSuccess }
            onLoadError={ handleDocumentLoadError }
          />
        </div>
      </div>
    </div>
  )
}


export default PDFViewer
